/**
 * Reports API - Report generation, templates, series, sharing
 * @module api/reports
 */

import { apiRequest, getAuthHeaders } from './core.js';

// =============================================================================
// IMMUTABLE REPORT GENERATION API
// =============================================================================

/**
 * Report API - Immutable audit-grade reports
 */
export const reportAPI = {
  // Get available report types
  getTypes: () => apiRequest('/reports/types'),

  // List reports with optional filters
  list: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.reportType) params.append('reportType', filters.reportType);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    const query = params.toString();
    return apiRequest(`/reports${query ? `?${query}` : ''}`);
  },

  // Get a specific report with its artifacts
  getById: (id) => apiRequest(`/reports/${id}`),

  // Generate a new report
  generate: (reportType, options = {}) => apiRequest('/reports', {
    method: 'POST',
    body: JSON.stringify({
      report_type: reportType,
      formats: [options.format || 'csv'],
      period_start: options.periodStart,
      period_end: options.periodEnd,
      filters: options.filters || {},
    }),
  }),

  // Regenerate a report (creates new version, supersedes old)
  regenerate: (reportId, options = {}) => apiRequest(`/reports/${reportId}/regenerate`, {
    method: 'POST',
    body: JSON.stringify({
      format: options.format || 'csv',
    }),
  }),

  // Get audit log for a report
  getAuditLog: (reportId) => apiRequest(`/reports/${reportId}/audit`),

  // Download a report artifact (streams file directly via API, no /uploads/ routing needed)
  download: async (reportId, format = 'csv') => {
    const authHeaders = getAuthHeaders();

    const response = await fetch(`/api/v1/reports/${reportId}/download?format=${format}&stream=1`, {
      headers: authHeaders
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || 'Download failed');
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition');
    let filename = `report_${reportId}.${format}`;
    if (disposition) {
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
    }

    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);

    return { ok: true, filename };
  },

  // Download EML file with PDF and CSV attachments
  downloadEML: async (reportId, emailConfig = {}) => {
    const baseUrl = '';

    const params = new URLSearchParams();
    if (emailConfig.senderEmail) params.append('from', emailConfig.senderEmail);
    if (emailConfig.senderName) params.append('fromName', emailConfig.senderName);
    if (emailConfig.recipients) params.append('to', emailConfig.recipients);

    const queryString = params.toString();
    const url = `${baseUrl}/api/v1/reports/${reportId}/eml${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || 'Failed to generate EML');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;

    const disposition = response.headers.get('Content-Disposition');
    let filename = `rapport_${reportId}_${new Date().toISOString().split('T')[0]}.eml`;
    if (disposition) {
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
    }

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);

    return { ok: true, filename };
  },
};

// =============================================================================
// REPORT SUBSCRIPTIONS API
// =============================================================================

/**
 * Report Subscription API - Scheduled report delivery
 */
export const reportSubscriptionAPI = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.report_type) params.append('report_type', filters.report_type);
    if (filters.schedule_type) params.append('schedule_type', filters.schedule_type);
    if (filters.is_enabled !== undefined) params.append('is_enabled', filters.is_enabled);
    const query = params.toString();
    return apiRequest(`/report-subscriptions${query ? `?${query}` : ''}`);
  },

  getById: (id) => apiRequest(`/report-subscriptions/${id}`),

  create: (data) => apiRequest('/report-subscriptions', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id, data) => apiRequest(`/report-subscriptions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  delete: (id) => apiRequest(`/report-subscriptions/${id}`, {
    method: 'DELETE',
  }),

  trigger: (id) => apiRequest(`/report-subscriptions/${id}/trigger`, {
    method: 'POST',
  }),
};

// =============================================================================
// REPORT TEMPLATES API (Level 1 of 3-level architecture)
// =============================================================================

/**
 * Report Template API - Predefined report templates
 */
export const reportTemplateAPI = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
    const query = params.toString();
    return apiRequest(`/report-templates${query ? `?${query}` : ''}`);
  },

  getById: (id) => apiRequest(`/report-templates/${id}`),

  getByCode: (code) => apiRequest(`/report-templates/code/${code}`),
};

// =============================================================================
// REPORT SERIES API (Level 2 of 3-level architecture)
// =============================================================================

/**
 * Report Series API - Recurring report collections
 */
export const reportSeriesAPI = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.frequency) params.append('frequency', filters.frequency);
    if (filters.is_pinned !== undefined) params.append('is_pinned', filters.is_pinned);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
    if (filters.search) params.append('search', filters.search);
    const query = params.toString();
    return apiRequest(`/report-series${query ? `?${query}` : ''}`);
  },

  getById: (id, options = {}) => {
    const params = new URLSearchParams();
    if (options.snapshot_limit) params.append('snapshot_limit', options.snapshot_limit);
    const query = params.toString();
    return apiRequest(`/report-series/${id}${query ? `?${query}` : ''}`);
  },

  getSnapshots: (seriesId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.year) params.append('year', options.year);
    const query = params.toString();
    return apiRequest(`/report-series/${seriesId}/snapshots${query ? `?${query}` : ''}`);
  },

  create: (data) => apiRequest('/report-series', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id, data) => apiRequest(`/report-series/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  togglePin: (id) => apiRequest(`/report-series/${id}/pin`, {
    method: 'POST',
  }),

  run: (id) => apiRequest(`/report-series/${id}/run`, {
    method: 'POST',
  }),

  delete: (id) => apiRequest(`/report-series/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// EPHEMERAL SHARING API (Secluded Snapshots)
// =============================================================================

/**
 * Share API - Ephemeral link sharing with PIN protection
 */
export const shareAPI = {
  create: (data) => apiRequest('/shares', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  list: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    const query = params.toString();
    return apiRequest(`/shares${query ? `?${query}` : ''}`);
  },

  getById: (shareId) => apiRequest(`/shares/${shareId}`),

  revoke: (shareId, reason = '') => apiRequest(`/shares/${shareId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),

  // Public methods (no auth required)
  getPublic: async (token) => {
    const baseUrl = '';
    const response = await fetch(`${baseUrl}/share/${token}`);
    return response.json();
  },

  unlock: async (token, pin) => {
    const baseUrl = '';
    const response = await fetch(`${baseUrl}/share/${token}/unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
    return response.json();
  },

  downloadArtifact: async (token, artifactName) => {
    const baseUrl = '';
    const response = await fetch(`${baseUrl}/share/${token}/download/${artifactName}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifactName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return { ok: true };
  },

  getShareUrl: (token) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/share/${token}`;
  },
};

// =============================================================================
// FLEET COCKPIT EXPORT API
// =============================================================================

/**
 * Fleet Cockpit Export API - Dashboard KPIs export
 */
export const fleetCockpitExportAPI = {
  preview: (period = 'month') => apiRequest(`/exports/cockpit/${period}/preview`),

  download: async (period = 'month') => {
    const baseUrl = '';
    const response = await fetch(`${baseUrl}/api/v1/exports/cockpit/${period}`, { headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `Cockpit_Flotte_${period}.xlsx`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
    return { ok: true };
  }
};
